import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  SearchSaleInvoiceClient,
  GetSaleInvoiceClient,
  DeleteSaleInvoiceClient,
  UpdateSaleInvoiceClient,
  CreateSaleInvoiceClient,
  ArchivingSaleInvoiceClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const searchSaleInvoiceClient = new SearchSaleInvoiceClient(SERVICE_URL)
const getSaleInvoiceClient = new GetSaleInvoiceClient(SERVICE_URL)
const createSaleInvoiceClient = new CreateSaleInvoiceClient(SERVICE_URL)
const deleteSaleInvoiceClient = new DeleteSaleInvoiceClient(SERVICE_URL)
const updateSaleInvoiceClient = new UpdateSaleInvoiceClient(SERVICE_URL)
const archivingSaleInvoiceClient = new ArchivingSaleInvoiceClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  isSent: false,
  documentdata: null,
  excel: null,
  saleinvoice: null,
  detail: null,
}

// Async Thunks
export const saleinvoiceGetAll = createAsyncThunk(
  'saleinvoice/getAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchSaleInvoiceClient.search(
        params.name,
        params.editStartDate,
        params.editEndDate,
        params.projectId,
        params.companyId,
        params.salePaymentStatusId,
        params.categoryId,
        params.invoiceKindId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setSaleinvoice(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const saleinvoiceExcel = createAsyncThunk(
  'saleinvoice/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchSaleInvoiceClient.excel(
        params.name,
        params.editStartDate,
        params.editEndDate,
        params.projectId,
        params.companyId,
        params.salePaymentStatusId,
        params.categoryId,
        params.invoiceKindId,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const saleinvoiceGetId = createAsyncThunk('saleinvoice/getId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await getSaleInvoiceClient.get(id)
    dispatch(setDetail(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const saleinvoiceDelete = createAsyncThunk(
  'saleinvoice/delete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deleteSaleInvoiceClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const saleinvoiceArchiving = createAsyncThunk(
  'saleinvoice/archiving',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await archivingSaleInvoiceClient.archive(params)
      dispatch(setArchived(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const saleinvoiceCreate = createAsyncThunk(
  'saleinvoice/create',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createSaleInvoiceClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const saleinvoiceUpdate = createAsyncThunk(
  'saleinvoice/update',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateSaleInvoiceClient.update(params)
      dispatch(setRedirect(response || params))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const saleinvoiceSlice = createSlice({
  name: 'saleinvoice',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setSaleinvoice: (state, action) => {
      state.saleinvoice = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setArchived: (state, action) => {
      state.isArchived = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    setDocumentData: (state, action) => {
      state.documentdata = action.payload
      state.loading = false
    },
    setSent: (state, action) => {
      state.isSent = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('saleinvoice/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setSaleinvoice,
  setDetail,
  setDeleted,
  setRedirect,
  setArchived,
  setExcel,
  setDocumentData,
  setSent,
  resetState,
} = saleinvoiceSlice.actions

// Selectors
export const selectSaleinvoiceLoading = (state) => state.saleinvoice.loading
export const selectSaleinvoiceError = (state) => state.saleinvoice.error
export const selectSaleinvoice = (state) => state.saleinvoice.saleinvoice
export const selectSaleinvoiceDetail = (state) => state.saleinvoice.detail
export const selectSaleinvoiceDeleted = (state) => state.saleinvoice.isDeleted
export const selectSaleinvoiceRedirect = (state) => state.saleinvoice.isRedirect
export const selectSaleinvoiceArchived = (state) => state.saleinvoice.isArchived
export const selectSaleinvoiceExcel = (state) => state.saleinvoice.excel
export const selectSaleinvoiceDocumentData = (state) => state.saleinvoice.documentdata
export const selectSaleinvoiceSent = (state) => state.saleinvoice.isSent

export default saleinvoiceSlice.reducer
